import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuthz } from "context/AuthzContext";
import { apiRequest } from "services/tenantUserAdminService";
import { TC, FS, FW } from "../tokens/text";

// ── Constants ─────────────────────────────────────────────────────────────────

const CARD_BG = "#ffffff";
const CARD_BORDER = "1px solid #d8e6f7";

const CHANGE_TYPE_LABELS = {
  director_appointed:             "Director Appointed",
  director_resigned:              "Director Resigned",
  director_changed:               "Director Details Changed",
  psc_appointed:                  "PSC Appointed",
  psc_ceased:                     "PSC Ceased",
  psc_changed:                    "PSC Details Changed",
  company_name_changed:           "Company Name Changed",
  company_status_changed:         "Company Status Changed",
  address_changed:                "Registered Address Changed",
  sic_codes_changed:              "SIC Codes Changed",
  accounts_dates_changed:         "Accounting Dates Changed",
  confirmation_statement_changed: "Confirmation Statement Changed",
};

const SEV_META = {
  high:   { color: "#f44336", bg: "rgba(255,71,87,0.10)",  label: "High",   icon: "warning"              },
  medium: { color: "#F7B731", bg: "rgba(247,183,49,0.10)", label: "Medium", icon: "notifications_active"  },
  low:    { color: "#4ECDC4", bg: "rgba(78,205,196,0.10)", label: "Low",    icon: "info"                  },
};

const TYPE_META = {
  inbound_email:   { color: "#a78bfa", icon: "mail",                label: "Email Received"   },
  director_change: { color: "#4ECDC4", icon: "person",              label: "Director Change"  },
  ch_change:       { color: "#a78bfa", icon: "business",            label: "Companies House"  },
  consent_expiring:{ color: "#F7B731", icon: "link_off",            label: "Consent Expiring" },
  consent_expired: { color: "#f44336", icon: "link_off",            label: "Consent Expired"  },
  bank_feed:       { color: "#4ECDC4", icon: "account_balance",     label: "Bank Feed"        },
  exception:       { color: "#F7B731", icon: "error_outline",       label: "Exception"        },
  vat:             { color: "#01B574", icon: "receipt_long",        label: "VAT"              },
  period_close:    { color: "#01B574", icon: "event_available",     label: "Period Close"     },
};

function getNotifRoute(n) {
  const clientId = n.clientId || n.client_id || null;
  const type     = n.type     || "";
  const change   = n.changeType || n.change_type || "";

  if (type === "inbound_email")
    return clientId
      ? { pathname: `/client-profiles/${encodeURIComponent(clientId)}`, state: { tab: 4 } }
      : "/client-profiles";

  if (type === "director_change" || ["director_appointed","director_resigned","director_changed"].includes(change))
    return clientId ? `/directors?clientId=${encodeURIComponent(clientId)}` : "/directors";

  if (["psc_appointed","psc_ceased","psc_changed"].includes(change))
    return clientId ? `/client-profiles/${encodeURIComponent(clientId)}` : "/client-profiles";

  if (["company_name_changed","company_status_changed","address_changed",
       "sic_codes_changed","accounts_dates_changed","confirmation_statement_changed"].includes(change))
    return clientId ? `/client-profiles/${encodeURIComponent(clientId)}` : "/client-profiles";

  if (["consent_expiring","consent_expired","bank_feed","consent"].includes(type)) return "/bank-feeds";
  if (["exception","transaction"].includes(type)) return "/exceptions";
  if (type === "vat") return "/vat-return";
  if (type === "period_close") return "/period-close";
  if (type === "ch_change") return clientId ? `/client-profiles/${encodeURIComponent(clientId)}` : "/client-profiles";
  return "/dashboard";
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function notifMeta(n) {
  const sevM  = SEV_META[n.severity]  || SEV_META.low;
  const typeM = TYPE_META[n.type]     || null;
  const color = typeM ? typeM.color : sevM.color;
  const bg    = sevM.bg;
  const icon  = typeM ? typeM.icon : sevM.icon;
  const typeLabel = typeM ? typeM.label : (CHANGE_TYPE_LABELS[n.changeType || n.change_type] || n.type || "Notification");
  const changeLabel = CHANGE_TYPE_LABELS[n.changeType || n.change_type] || n.changeType || n.change_type || "";
  return { color, bg, icon, typeLabel, changeLabel };
}

// ── Filter tabs ───────────────────────────────────────────────────────────────

const FILTERS = [
  { key: "all",    label: "All"       },
  { key: "unread", label: "Unread"    },
  { key: "high",   label: "🔴 High"   },
  { key: "medium", label: "🟡 Medium" },
  { key: "low",    label: "🔵 Low"    },
];

// ── Row component ─────────────────────────────────────────────────────────────

function NotifRow({ n, onMarkRead, onDelete, onNavigate }) {
  const { color, bg, icon, typeLabel, changeLabel } = notifMeta(n);
  const ago = timeAgo(n.createdAt || n.detectedAt);

  return (
    <VuiBox
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{
        background: n.read ? "transparent" : bg,
        border: `1px solid ${n.read ? "#e4eef8" : color + "33"}`,
        borderRadius: 2,
        p: "14px 16px",
        mb: 1.5,
        transition: "all 0.2s",
        opacity: n.read ? 0.7 : 1,
        "&:hover": { background: n.read ? "rgba(0,117,255,0.04)" : bg, opacity: 1 },
      }}
    >
      {/* Left: icon + dot */}
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 38, height: 38, borderRadius: "50%",
          background: `${color}22`, border: `1px solid ${color}44`,
          flexShrink: 0, mr: 2, mt: 0.3, position: "relative",
        }}
      >
        <Icon sx={{ color, fontSize: "1.1rem !important" }}>{icon}</Icon>
        {!n.read && (
          <span style={{
            position: "absolute", top: 0, right: 0,
            width: 9, height: 9, borderRadius: "50%",
            background: color, border: "2px solid #ffffff",
          }} />
        )}
      </VuiBox>

      {/* Middle: content */}
      <VuiBox flex={1} minWidth={0}>
        <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.4}>
          {/* Type chip */}
          <span style={{
            background: `${color}1a`, color, border: `1px solid ${color}44`,
            borderRadius: 4, padding: "1px 8px", fontSize: 11, fontWeight: 700,
          }}>{typeLabel}</span>

          {/* Client name */}
          {(n.clientName || n.companyNumber) && (
            <VuiTypography sx={{ color: TC.primary, fontSize: 13, fontWeight: 700 }}>
              {n.clientName || n.companyNumber}
            </VuiTypography>
          )}

          {/* Severity chip */}
          {n.severity && (
            <span style={{
              background: `${SEV_META[n.severity]?.color || "#4ECDC4"}18`,
              color: SEV_META[n.severity]?.color || "#4ECDC4",
              border: `1px solid ${SEV_META[n.severity]?.color || "#4ECDC4"}33`,
              borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 700,
            }}>{n.severity?.toUpperCase()}</span>
          )}

          {/* Blocking badge */}
          {n.isBlocking && (
            <span style={{
              background: "rgba(255,71,87,0.15)", color: "#f44336",
              border: "1px solid rgba(255,71,87,0.4)",
              borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 700,
            }}>BLOCKING</span>
          )}

          <VuiTypography sx={{ color: TC.secondary, fontSize: 11, ml: "auto" }}>
            {ago}
          </VuiTypography>
        </VuiBox>

        {/* Change label */}
        {changeLabel && changeLabel !== typeLabel && (
          <VuiTypography sx={{ color: TC.midtone, fontSize: 12, mb: 0.3 }}>
            {changeLabel}
          </VuiTypography>
        )}

        {/* Email subject / message for inbound_email */}
        {n.type === "inbound_email" && (
          <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mb: 0.3 }}>
            ✉️ {n.subject || "New email"}{n.attachmentCount > 0 ? ` · 📎 ${n.attachmentCount}` : ""}
          </VuiTypography>
        )}

        {/* Suggested action */}
        {(n.suggestedAction || n.suggested_action) && (
          <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mb: 0.3 }}>
            💡 {n.suggestedAction || n.suggested_action}
          </VuiTypography>
        )}

        {/* Impact areas */}
        {Array.isArray(n.impactAreas || n.impact_areas) && (n.impactAreas || n.impact_areas).length > 0 && (
          <VuiBox display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
            {(n.impactAreas || n.impact_areas).map((area) => (
              <span key={area} style={{
                background: "#f4f8ff", color: TC.secondary,
                borderRadius: 4, padding: "1px 7px", fontSize: 10,
              }}>{area}</span>
            ))}
          </VuiBox>
        )}

        {/* Old → New value */}
        {(n.newValue || n.new_value) && (n.newValue || n.new_value) !== "null" && (
          <VuiTypography sx={{ color: TC.secondary, fontSize: 11, mt: 0.4 }}>
            {(n.oldValue || n.old_value) && (n.oldValue || n.old_value) !== "null" && (n.oldValue || n.old_value) !== ""
              ? `${n.oldValue || n.old_value} → ` : ""}
            {n.newValue || n.new_value}
          </VuiTypography>
        )}

        {/* Timestamp row */}
        <VuiTypography sx={{ color: TC.tertiary, fontSize: 11, mt: 0.5 }}>
          {fmtDateTime(n.createdAt || n.detectedAt)}
          {n.read && n.readAt && ` · Read ${timeAgo(n.readAt)}`}
        </VuiTypography>
      </VuiBox>

      {/* Right: actions */}
      <VuiBox display="flex" alignItems="center" gap={1} ml={2} flexShrink={0}>
        <Tooltip title="Go to related page">
          <VuiButton
            size="small" variant="outlined" color="info"
            sx={{ fontSize: 11, py: 0.4, px: 1.2, minWidth: 0, bordercolor: "#7a94b0" }}
            onClick={() => onNavigate(n)}
          >
            <Icon sx={{ fontSize: "14px !important" }}>open_in_new</Icon>
          </VuiButton>
        </Tooltip>

        {!n.read && (
          <Tooltip title="Mark as read">
            <VuiButton
              size="small" variant="outlined"
              sx={{ fontSize: 11, py: 0.4, px: 1.2, minWidth: 0, borderColor: `${color}55`, color }}
              onClick={() => onMarkRead(n.id)}
            >
              <Icon sx={{ fontSize: "14px !important" }}>done</Icon>
            </VuiButton>
          </Tooltip>
        )}

        <Tooltip title="Delete">
          <VuiButton
            size="small" variant="outlined" color="error"
            sx={{ fontSize: 11, py: 0.4, px: 1.2, minWidth: 0 }}
            onClick={() => onDelete(n.id)}
          >
            <Icon sx={{ fontSize: "14px !important" }}>delete_outline</Icon>
          </VuiButton>
        </Tooltip>
      </VuiBox>
    </VuiBox>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function NotificationsPage() {
  const { tenantId } = useAuthz();
  const history = useHistory();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState("all");
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/notifications`,
        { query: { limit: "200" } }
      );
      const list = Array.isArray(data?.notifications) ? data.notifications : [];
      list.sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        return (b.createdAt || b.detectedAt || "").localeCompare(a.createdAt || a.detectedAt || "");
      });
      // Filter out director_appointed and psc_appointed notifications per user request
      // Also exclude notifications dismissed for this user (backend sets _dismissedForUser)
      const filtered = list.filter((n) => {
        const change = n.changeType || n.change_type || "";
        const dismissed = !!n._dismissedForUser;
        return !dismissed && change !== "director_appointed" && change !== "psc_appointed";
      });
      setNotifications(filtered);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  async function handleMarkRead(id) {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    await apiRequest(
      `/tenant/${encodeURIComponent(tenantId)}/notifications/${encodeURIComponent(id)}/read`,
      { method: "PATCH" }
    ).catch(() => {});
  }

  async function handleDelete(id) {
    setNotifications(prev => prev.filter(n => n.id !== id));
    await apiRequest(
      `/tenant/${encodeURIComponent(tenantId)}/notifications/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ).catch(() => {});
  }

  async function handleMarkAllRead() {
    setActionLoading(true);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    await apiRequest(
      `/tenant/${encodeURIComponent(tenantId)}/notifications/read-all`,
      { method: "PATCH" }
    ).catch(() => {});
    setActionLoading(false);
  }

  async function handleDeleteAllRead() {
    setActionLoading(true);
    await apiRequest(
      `/tenant/${encodeURIComponent(tenantId)}/notifications`,
      { method: "DELETE" }
    ).catch(() => {});
    setNotifications(prev => prev.filter(n => !n.read));
    setActionLoading(false);
  }

  async function handleNavigate(n) {
    // When a notification is opened, delete it from the tenant inbox and refresh
    try {
      await apiRequest(`/tenant/${encodeURIComponent(tenantId)}/notifications/${encodeURIComponent(n.id)}`, { method: "DELETE" });
      setNotifications(prev => prev.filter(x => x.id !== n.id));
    } catch (_) {
      // ignore
    }

    const route = getNotifRoute(n);
    if (typeof route === "object") {
      history.push(route.pathname, route.state);
    } else {
      history.push(route);
    }
  }

  // ── Derived ──────────────────────────────────────────────────────────────────

  const filtered = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "high")   return n.severity === "high";
    if (filter === "medium") return n.severity === "medium";
    if (filter === "low")    return n.severity === "low";
    return true;
  });

  const unreadCount  = notifications.filter(n => !n.read).length;
  const highCount    = notifications.filter(n => n.severity === "high" && !n.read).length;
  const blockingCount= notifications.filter(n => n.isBlocking && !n.read).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* Header */}
        <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>
              🔔 Notifications
            </VuiTypography>
            <VuiTypography sx={{ color: TC.tertiary, fontSize: 13, mt: 0.3 }}>
              {loading ? "Loading…" : `${notifications.length} total · ${unreadCount} unread`}
              {highCount > 0 && (
                <span style={{ color: "#f44336", fontWeight: 700 }}>
                  {" "}· {highCount} high severity
                </span>
              )}
              {blockingCount > 0 && (
                <span style={{ color: "#f44336", fontWeight: 700 }}>
                  {" "}· {blockingCount} blocking
                </span>
              )}
            </VuiTypography>
          </VuiBox>

          <VuiBox display="flex" gap={1.5} flexWrap="wrap">
            <VuiButton
              size="small" variant="outlined" color="info"
              startIcon={<Icon>refresh</Icon>}
              sx={{ fontSize: 12 }}
              onClick={load}
              disabled={loading}
            >
              Refresh
            </VuiButton>
            {unreadCount > 0 && (
              <VuiButton
                size="small" variant="outlined" color="info"
                startIcon={<Icon>done_all</Icon>}
                sx={{ fontSize: 12 }}
                onClick={handleMarkAllRead}
                disabled={actionLoading}
              >
                Mark All Read
              </VuiButton>
            )}
            <VuiButton
              size="small" variant="outlined" color="error"
              sx={{ fontSize: 12 }}
              startIcon={<Icon>delete_sweep</Icon>}
              onClick={handleDeleteAllRead}
              disabled={actionLoading || notifications.filter(n => n.read).length === 0}
            >
              Delete Read
            </VuiButton>
          </VuiBox>
        </VuiBox>

        <Grid container spacing={3}>
          {/* ── Left: filter sidebar ── */}
          <Grid item xs={12} md={3}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={2.5}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 }}>
                  Filter
                </VuiTypography>
                {FILTERS.map(f => {
                  const count = f.key === "all"    ? notifications.length
                              : f.key === "unread" ? unreadCount
                              : notifications.filter(n => n.severity === f.key).length;
                  const isActive = filter === f.key;
                  return (
                    <VuiBox
                      key={f.key}
                      role="button"
                      tabIndex={0}
                      display="flex" justifyContent="space-between" alignItems="center"
                      sx={{
                        px: 1.5, py: 1, mb: 0.5, borderRadius: 2, cursor: "pointer",
                        background: isActive ? "rgba(0,87,216,0.08)" : "transparent",
                        border: isActive ? "1px solid rgba(0,87,216,0.2)" : "1px solid transparent",
                        "&:hover": { background: isActive ? "rgba(0,87,216,0.08)" : "rgba(0,117,255,0.04)" },
                        transition: "all 0.15s",
                      }}
                      onClick={() => setFilter(f.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setFilter(f.key);
                        }
                      }}
                    >
                      <VuiTypography sx={{ color: isActive ? TC.primary : TC.secondary, fontSize: FS.sm, fontWeight: isActive ? FW.bold : FW.normal }}>
                        {f.label}
                      </VuiTypography>
                      {count > 0 && (
                        <span style={{
                          background: isActive ? "rgba(0,87,216,0.12)" : "rgba(0,0,0,0.06)",
                          color: TC.primary,
                          borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700,
                        }}>{count}</span>
                      )}
                    </VuiBox>
                  );
                })}

                <Divider sx={{ borderColor: "#d8e6f7", my: 2 }} />

                {/* Stats summary */}
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 }}>
                  Summary
                </VuiTypography>
                {[
                  { label: "Unread",   value: unreadCount,   color: "#4ECDC4" },
                  { label: "High",     value: notifications.filter(n => n.severity === "high").length,   color: "#f44336" },
                  { label: "Medium",   value: notifications.filter(n => n.severity === "medium").length, color: "#F7B731" },
                  { label: "Blocking", value: blockingCount, color: "#f44336" },
                  { label: "Read",     value: notifications.filter(n => n.read).length, color: TC.secondary },
                ].map(({ label, value, color }) => (
                  <VuiBox key={label} display="flex" justifyContent="space-between" mb={0.8}>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 12 }}>{label}</VuiTypography>
                    <VuiTypography sx={{ color, fontSize: 13, fontWeight: 700 }}>{value}</VuiTypography>
                  </VuiBox>
                ))}
              </VuiBox>
            </Card>
          </Grid>

          {/* ── Right: notification list ── */}
          <Grid item xs={12} md={9}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                {loading ? (
                  <LoadingSpinner label="Loading notifications…" />
                ) : filtered.length === 0 ? (
                  <VuiBox py={5} textAlign="center">
                    <Icon sx={{ color: TC.tertiary, fontSize: "3rem !important", mb: 1 }}>notifications_none</Icon>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 15 }}>
                      {filter === "all" ? "No notifications yet." : `No ${filter} notifications.`}
                    </VuiTypography>
                  </VuiBox>
                ) : (
                  <>
                    <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <VuiTypography sx={{ color: TC.secondary, fontSize: 12 }}>
                        {filtered.length} notification{filtered.length !== 1 ? "s" : ""}
                        {filter !== "all" ? ` (${filter})` : ""}
                      </VuiTypography>
                    </VuiBox>
                    {filtered.map(n => (
                      <NotifRow
                        key={n.id}
                        n={n}
                        onMarkRead={handleMarkRead}
                        onDelete={handleDelete}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </>
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NotificationsPage;

